import React, { useState, useEffect } from 'react'

import { useHistory, useLocation } from 'react-router-dom'

import { useTranslation } from 'react-i18next'
import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType
} from '@globaleris/ui.library'

import { Button, Dialog, DialogActions, DialogContent, Typography } from '@material-ui/core'
import { useHttpError } from '../../../hooks'
import {
  AnnouncementItemType,
  AnnouncementItemTypes,
  SubjectAnnouncementModel,
  TabsClientType
} from '../clients.model'
import { getCustomerDomain } from '../../../redux'
import { Pages } from '../../../model/RBAC/constants'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import { Pageview } from '@material-ui/icons'
import { NonAuthRoutes } from '../../../model'
import { enumKeyToValue, enumToSelectOptionArray } from '../../../helpers'
import { ProcedureTypes } from '../../../api/Argentia/enums'

export interface AnnouncementDialogTableProps {
  announcements: SubjectAnnouncementModel
  itemType: AnnouncementItemTypes
  tableTitle: string | undefined
  subjectType: number
  onClose: () => void
}

export const AnnouncementDialogTable = ({
  announcements,
  itemType,
  tableTitle,
  subjectType,
  onClose
}: AnnouncementDialogTableProps) => {
  const history = useHistory()

  const [t] = useTranslation(['globalerisLibrary', 'clients', 'enums'])

  const { showError, clearError, errorProps } = useHttpError()

  const handleClose = () => {
    onClose()
  }

  // DataTable Config Starts

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'subjectName',
      label: t('clients:landingPage.announcement.dataTable.columns.subjectName'),
      type: DataType.TEXT,
      display:
        itemType === AnnouncementItemType.SUBJECT_WITHOUT_CONTRACTS ||
        itemType === AnnouncementItemType.CONTRACTS_NEXT_EXPIRATION
    },
    {
      name: 'fiscalDocument',
      label: t('clients:landingPage.announcement.dataTable.columns.fiscalDocument'),
      type: DataType.TEXT,
      display:
        itemType === AnnouncementItemType.SUBJECT_WITHOUT_CONTRACTS ||
        itemType === AnnouncementItemType.CONTRACTS_NEXT_EXPIRATION
    },
    {
      name: 'contractCode',
      label: t('clients:landingPage.announcement.dataTable.columns.contractCode'),
      type: DataType.TEXT,
      display: itemType === AnnouncementItemType.CONTRACTS_NEXT_EXPIRATION
    },
    {
      name: 'subject',
      label: t('clients:landingPage.announcement.dataTable.columns.subjectName'),
      type: DataType.TEXT,
      display:
        itemType === AnnouncementItemType.PROCEDURES_IN_PROGRESS ||
        itemType === AnnouncementItemType.INCIDENT_PROCEDURES
    },
    {
      name: 'comunicationDate',
      label: t('clients:landingPage.announcement.dataTable.columns.comunicationDate'),
      type: DataType.DATE,
      display:
        itemType === AnnouncementItemType.PROCEDURES_IN_PROGRESS ||
        itemType === AnnouncementItemType.INCIDENT_PROCEDURES
    },
    {
      name: 'cups',
      label: t('clients:landingPage.announcement.dataTable.columns.cups'),
      type: DataType.TEXT,
      display:
        itemType === AnnouncementItemType.PROCEDURES_IN_PROGRESS ||
        itemType === AnnouncementItemType.INCIDENT_PROCEDURES
    },
    {
      name: 'type',
      label: t('clients:landingPage.announcement.dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(ProcedureTypes, 'enums:ProcedureTypes'),
      display: itemType === AnnouncementItemType.PROCEDURES_IN_PROGRESS
    },
    {
      name: 'subjectId',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'cupsId',
      display: false,
      type: DataType.NUMBER
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    canInsert: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('clients:landingPage.announcement.dataTable.analytics')
    },
    //fabLabel: t('flexibility:dataTable.fab'),
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('clients:landingPage.announcement.dataTable.tooltips.view'),
        onClick: (row: any) => {
          switch (itemType) {
            case AnnouncementItemType.CONTRACTS_NEXT_EXPIRATION:
              history.push(NonAuthRoutes.ClientFile, {
                clientID: row.subjectId
              })
              break
            case AnnouncementItemType.SUBJECT_WITHOUT_CONTRACTS: {
              history.push(NonAuthRoutes.ClientFile, {
                clientID: row.subjectId
              })
              break
            }
            case AnnouncementItemType.PROCEDURES_IN_PROGRESS: {
              history.push(NonAuthRoutes.ClientFile, {
                clientID: row.subjectId
              })
              break
            }
            case AnnouncementItemType.INCIDENT_PROCEDURES: {
              history.push(NonAuthRoutes.ClientFile, {
                clientID: row.subjectId
              })
              break
            }
          }
        }
      }
    ]
  }

  // DataTable Config Ends

  // Functions Starts

  const getDataForTable = (itemType: AnnouncementItemTypes) => {
    let items: any
    //setTableTitle(t(`clients:landingPage.announcement.${itemType}.title`));
    switch (itemType) {
      case AnnouncementItemType.CONTRACTS_NEXT_EXPIRATION: {
        items = announcements.contractsSubjectNextExpiration
        break
      }
      case AnnouncementItemType.SUBJECT_WITHOUT_CONTRACTS: {
        items = announcements.activeSubjectWithoutContracts
        break
      }
      case AnnouncementItemType.PROCEDURES_IN_PROGRESS: {
        items = announcements.proceduresInProgress.map(procedure => ({
          ...procedure,
          type: enumKeyToValue(ProcedureTypes, procedure.type)
        }))
        break
      }
      case AnnouncementItemType.INCIDENT_PROCEDURES: {
        items = announcements.incidentProcedures
        break
      }
    }
    return items
  }

  // Functions Ends

  // Hooks Starts
  // useEffect(() => {
  //   fetchFlexibilitiesList(props.commodity.activeCommodity, props.calculationType);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [props.commodity, props.calculationType]);

  // Hooks Ends

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      disableBackdropClick
      disableEscapeKeyDown
      open={true}
      onClose={() => {}}
      //className={classes.root}
    >
      <DialogContent>
        <Typography variant="h1" color="secondary">
          {t('clients:landingPage.announcement.title')}
        </Typography>
        <DataTable
          title={tableTitle}
          data={getDataForTable(itemType)}
          columns={columns}
          options={{
            ...options,
            analytics: {
              customer: getCustomerDomain(),
              view: t('clients:landingPage.announcement.dataTable.analytics')
            }
          }}
          info={DataTableErrorHandler(errorProps)}
          userPermissions={getUserPermissions(Pages.Client)}
        />
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose}>
          {t('clients:landingPage.announcement.dataTable.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
